import React, { useEffect, useState } from 'react';
import { Splitter, Select, Card, Spin, Row, Col, message, Typography } from 'antd';
import { RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Tooltip, Legend, BarChart, Bar, XAxis, YAxis } from 'recharts'; // Import recharts components
import axiosInstance from '../../utils/axiosInstance';

const { Option } = Select;
const HIGHLIGHT_COLOR = '#C7F6C7';
const COLORS = ['#465892', '#546DA6', '#BBC1C6', '#968EAF', '#6D5593', '#543D7B'];

const percentageFormatter = (value) => `${(value * 100).toFixed(2)}%`;
const decimalFormatter = (value) => {
  if (Number.isInteger(value)) {
    return value.toFixed(0);
  }
  return value?.toFixed(2);
};

const groupedStats = [
  {
    group: "Points & Ratings",
    stats: [
      { key: "total_pts", label: "Points" },
      { key: "ortg", label: "Offensive Rating" },
      { key: "drtg", label: "Defensive Rating" },
      { key: "net_rating", label: "Net Rating" },
    ],
  },
  {
    group: "Shooting",
    stats: [
      { key: "fga", label: "Field Goals Attempted" },
      { key: "fgm", label: "Field Goals Made" },
      { key: "three_pa", label: "3-Point Field Goals Attempted" },
      { key: "three_pm", label: "3-Point Field Goals Made" },
      { key: "fta", label: "Free Throws Attempted" },
      { key: "ftm", label: "Free Throws Made" },
    ],
  },
  {
    group: "Shooting Percentages",
    stats: [
      { key: "team_fg_pct", label: "Field Goal %", isPercentage: true },
      { key: "team_three_pct", label: "3-Point Field Goal %", isPercentage: true },
      { key: "team_ft_pct", label: "Free Throw %", isPercentage: true },
      { key: "efg_pct", label: "Effective Field Goal %", isPercentage: true },
      { key: "ts_pct", label: "True Shooting %", isPercentage: true },
    ],
  },
  {
    group: "Efficiency",
    stats: [
      { key: "oe", label: "Offensive Efficiency" },
      { key: "eps", label: "Efficient Points Scored" },
      { key: "eop", label: "Efficient Offensive Production" },
    ],
  },
  {
    group: "Possessions",
    stats: [
      { key: "team_poss", label: "Possessions" },
      { key: "pts_poss", label: "Points per Possession" },
      { key: "eps_poss", label: "EPS per Possession" },
      { key: "eop_poss", label: "EOP per Possession" },
    ],
  },
  {
    group: "Rebounds",
    stats: [
      { key: "reb", label: "Rebounds" },
      { key: "oreb", label: "Offensive Rebounds" },
      { key: "dreb", label: "Defensive Rebounds" },
      { key: "oreb_pct", label: "Offensive Rebound %", isPercentage: true },
      { key: "dreb_pct", label: "Defensive Rebound %", isPercentage: true },
    ],
  },
  {
    group: "Defensive Impact",
    stats: [
      { key: "stl", label: "Steals" },
      { key: "blk", label: "Blocks" },
    ],
  },
  {
    group: "Playmaking",
    stats: [
      { key: "ast", label: "Assists" },
      { key: "tov", label: "Turnovers" },
      { key: "ast_to", label: "Assist-to-Turnover Ratio" },
    ],
  },
];

const TeamStatsComparison = ({ gameId }) => {
  const [teams, setTeams] = useState([]);
  const [team1Stats, setTeam1Stats] = useState({});
  const [team2Stats, setTeam2Stats] = useState({});
  const [selectedTeam1, setSelectedTeam1] = useState(null);
  const [selectedTeam2, setSelectedTeam2] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedStats, setSelectedStats] = useState([]); // New state for selected stats

  useEffect(() => {
    const fetchTeamsAndStats = async () => {
      setLoading(true);
      try {
        const [derivedResponse, analyticalResponse] = await Promise.all([
          axiosInstance.get(`/api/games/${gameId}/der_team_stats/`),
          axiosInstance.get(`/api/games/${gameId}/ana_team_stats/`),
        ]);

        const combinedStats = derivedResponse.data.map((teamStat) => ({
          ...teamStat,
          ...analyticalResponse.data.find((anaStat) => anaStat.team === teamStat.team),
        }));

        const uniqueTeams = combinedStats.map((stat) => stat.team);
        setTeams(uniqueTeams);

        if (uniqueTeams.length >= 2) {
          setSelectedTeam1(uniqueTeams[0]);
          setSelectedTeam2(uniqueTeams[1]);
          setTeam1Stats(combinedStats.find((stat) => stat.team === uniqueTeams[0]) || {});
          setTeam2Stats(combinedStats.find((stat) => stat.team === uniqueTeams[1]) || {});
        }
      } catch (error) {
        message.error('Failed to fetch team stats');
      } finally {
        setLoading(false);
      }
    };

    fetchTeamsAndStats();
  }, [gameId]);

  useEffect(() => {
    if (selectedTeam1 || selectedTeam2) {
      const fetchTeamStats = async () => {
        setLoading(true);
        try {
          const [derivedResponse, analyticalResponse] = await Promise.all([
            axiosInstance.get(`/api/games/${gameId}/der_team_stats/`),
            axiosInstance.get(`/api/games/${gameId}/ana_team_stats/`),
          ]);

          const combinedStats = derivedResponse.data.map((teamStat) => ({
            ...teamStat,
            ...analyticalResponse.data.find((anaStat) => anaStat.team === teamStat.team),
          }));

          setTeam1Stats(combinedStats.find((stat) => stat.team === selectedTeam1) || {});
          setTeam2Stats(combinedStats.find((stat) => stat.team === selectedTeam2) || {});
        } catch (error) {
          message.error('Failed to fetch team stats');
        } finally {
          setLoading(false);
        }
      };

      fetchTeamStats();
    }
  }, [selectedTeam1, selectedTeam2, gameId]);


  const renderComparisonTable = () => (
    <table style={{ width: '100%', textAlign: 'center', borderCollapse: 'collapse' }}>
      <thead>
        <tr>
          <th style={{ padding: '10px', borderBottom: '2px solid #ddd' }}></th>
          <th style={{ padding: '10px', borderBottom: '2px solid #ddd' }}>Team 1</th>
          <th style={{ padding: '10px', borderBottom: '2px solid #ddd' }}>Team 2</th>
        </tr>
      </thead>
      <tbody>
        {groupedStats.map(({ group, stats }) => (
          <React.Fragment key={group}>
            <tr>
              <th
                colSpan="3"
                style={{
                  background: '#f0f0f0',
                  padding: '10px',
                  borderBottom: '2px solid #ddd',
                  fontWeight: 'bold',
                }}
              >
                {group}
              </th>
            </tr>
            {stats.map(({ key, label, isPercentage }) => (
              <tr key={key}>
                <td style={{ padding: '8px', borderBottom: '1px solid #ddd' }}>{label}</td>
                <td
                  style={{
                    padding: '8px',
                    borderBottom: '1px solid #ddd',
                    backgroundColor:
                    key === 'tov' && team1Stats[key] < team2Stats[key]
                      ? HIGHLIGHT_COLOR
                      : key !== 'tov' && team1Stats[key] > team2Stats[key]
                      ? HIGHLIGHT_COLOR
                      : 'transparent',
                }}
                >
                  {team1Stats[key] != null
                    ? isPercentage
                      ? percentageFormatter(team1Stats[key])
                      : decimalFormatter(team1Stats[key])
                    : 'N/A'}
                </td>
                <td
                  style={{
                    padding: '8px',
                    borderBottom: '1px solid #ddd',
                    backgroundColor:
                    key === 'tov' && team2Stats[key] < team1Stats[key]
                      ? HIGHLIGHT_COLOR
                      : key !== 'tov' && team2Stats[key] > team1Stats[key]
                      ? HIGHLIGHT_COLOR
                      : 'transparent',
                }}
                >
                  {team2Stats[key] != null
                    ? isPercentage
                      ? percentageFormatter(team2Stats[key])
                      : decimalFormatter(team2Stats[key])
                    : 'N/A'}
                </td>
              </tr>
            ))}
          </React.Fragment>
        ))}
      </tbody>
    </table>
  );

  const renderBarChart = () => {
    const data = selectedStats.map((statKey) => {
      const statConfig = groupedStats
        .flatMap((group) => group.stats)
        .find((stat) => stat.key === statKey);
  
      const isPercentage = statConfig?.isPercentage || false;
  
      return {
        stat: statConfig?.label || statKey,
        Team1: team1Stats[statKey] || 0,
        Team2: team2Stats[statKey] || 0,
        isPercentage, // Pass this to know if formatting is needed
      };
    });
  
    return (
      <BarChart width={550} height={450} data={data}>
        <XAxis dataKey="stat" />
        <YAxis tickFormatter={(value) =>
          data.some((d) => d.isPercentage) ? percentageFormatter(value) : decimalFormatter(value)
        } />
        <Tooltip
          formatter={(value, name, props) => {
            const isPercentage = props.payload.isPercentage;
            return isPercentage ? percentageFormatter(value) : decimalFormatter(value);
          }}
        />
        <Bar dataKey="Team1" fill={COLORS[0]} />
        <Bar dataKey="Team2" fill={COLORS[2]} />
      </BarChart>
    );
  };

  return (
    <div style={{ padding: '20px' }}>
      {loading ? (
        // Display a centered loading spinner while loading
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <Spin size="large" />
        </div>
      ) : (
        <>
          {/* Team Selection */}
          <Row gutter={[16, 16]} style={{ marginBottom: '20px' }}>
            <Col span={12}>
              <Card title="Select Team 1">
                <Select
                  value={selectedTeam1}
                  style={{ width: '100%' }}
                  onChange={setSelectedTeam1}
                >
                  {teams.map((team) => (
                    <Option key={team} value={team}>
                      {team}
                    </Option>
                  ))}
                </Select>
              </Card>
            </Col>
            <Col span={12}>
              <Card title="Select Team 2">
                <Select
                  value={selectedTeam2}
                  style={{ width: '100%' }}
                  onChange={setSelectedTeam2}
                >
                  {teams.map((team) => (
                    <Option key={team} value={team}>
                      {team}
                    </Option>
                  ))}
                </Select>
              </Card>
            </Col>
          </Row>

          <Splitter style={{ height: '100%', display: 'flex' }}>
            {/* Left Panel for Comparison Table */}
            <Splitter.Panel collapsible style={{ flex: 1, minWidth: '300px' }}>
              <Card title="Team Comparison Table">{renderComparisonTable()}</Card>
            </Splitter.Panel>

            {/* Right Panel with Four Factors Radar Chart */}
            <Splitter.Panel style={{ flex: 1 }}>
            <Card 
                title="Four Factors Analysis" 
                style={{ flex: 1, marginBottom: '8px'}}
            >
                <RadarChart
                cx="50%"
                cy="50%"
                outerRadius="80%"
                width={550}
                height={450}
                data={[
                    {
                    factor: "4F",
                    Team1: team1Stats.four_factors || 0,
                    Team2: team2Stats.four_factors || 0,
                    },
                    {
                    factor: "eFG%",
                    Team1: team1Stats.efg_pct || 0,
                    Team2: team2Stats.efg_pct || 0,
                    },
                    {
                    factor: "OREB%",
                    Team1: team1Stats.oreb_pct || 0,
                    Team2: team2Stats.oreb_pct || 0,
                    },
                    {
                    factor: "TOV%",
                    Team1: team1Stats.tov_pct || 0,
                    Team2: team2Stats.tov_pct || 0,
                    },
                    {
                    factor: "FTF",
                    Team1: team1Stats.ft_factor || 0,
                    Team2: team2Stats.ft_factor || 0,
                    },
                ]}
                >
                <PolarGrid />
                <PolarAngleAxis dataKey="factor" />
                <PolarRadiusAxis tickFormatter={decimalFormatter} />
                <Radar
                    name="Team 1"
                    dataKey="Team1"
                    stroke={COLORS[0]}
                    fill={COLORS[0]}
                    fillOpacity={0.6}
                />
                <Radar
                    name="Team 2"
                    dataKey="Team2"
                    stroke={COLORS[2]}
                    fill={COLORS[2]}
                    fillOpacity={0.6}
                />
                <Tooltip formatter={decimalFormatter} />
                <Legend
                    layout="horizontal"
                    verticalAlign="bottom"
                    align="center"
                    formatter={(value) => (
                    <span style={{ color: value === "Team 1" ? COLORS[0] : COLORS[2] }}>
                        {value}
                    </span>
                    )}
                />
                </RadarChart>
            </Card>

            {/* Center Right Panel for Dynamic Bar Chart */}
                <Card
                title="Compare Selected Metrics"
                style={{ marginBottom: '10px' }}
                >
                <Select
                    mode="multiple"
                    allowClear
                    style={{ width: '100%' }}
                    placeholder="Select stats to compare"
                    onChange={setSelectedStats} // Update selected stats dynamically
                >
                    {groupedStats.flatMap((group) => group.stats).map((stat) => (
                    <Option key={stat.key} value={stat.key}>
                        {stat.label}
                    </Option>
                    ))}
                </Select>

                {selectedStats.length > 0 ? (
                    <div style={{ marginTop: '20px' }}> {/* Add marginTop here */}
                    {renderBarChart()}
                    </div>
                ) : (
                    <Typography.Text type="secondary" style={{ marginTop: '20px', display: 'block' }}>
                    Select stats from the above list to start dynamically and visually compare both teams.
                    </Typography.Text>
                )}
                </Card>
                </Splitter.Panel>
        </Splitter>
        </>
      )}
    </div>
  );
};

export default TeamStatsComparison;
